// Contract held on the subscription: recurring lines, covered accounts and the
// end-of-contract lead time (plans/SPEC-subscription-features.md D4-D14, D18).
// Kept apart from types.ts, which is already past the size budget.

export const LINE_BASES = ['fixed', 'per_sensor', 'per_calibration', 'per_sim'] as const;
export type LineBasis = typeof LINE_BASES[number];

export interface SubscriptionLine {
  id: string;
  subscriptionId: string;
  sku: string;
  description: string | null;
  quantity: string;
  unitPrice: string;
  discount: string;
  basis: LineBasis;
  sortOrder: number | null;
  verified: boolean;
  verifiedBy: string | null;
  verifiedByName?: string | null;
  verifiedAt: string | null;
  endedAt: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface SubscriptionLineInput {
  sku: string;
  description?: string | null;
  quantity: number;
  unitPrice: number;
  discount?: number;
  basis: LineBasis;
  sortOrder?: number;
}

export type SubscriptionLineUpdate = Partial<SubscriptionLineInput> & { endedAt?: string | null };

export interface SubscriptionCoveredAccount {
  accountId: string;
  accountNumber: string | null;
  accountName: string | null;
  reason: string;
  addedBy: string | null;
  addedByName?: string | null;
  addedAt: string;
}

/** Money as 2-decimal strings; pct is the ratio × 100, unrounded thresholds server-side. */
export interface SubscriptionCeiling {
  billedToDate: string | null;
  totalValue: string | null;
  pct: number | null;
}

/** Fields `GET /v4/subscriptions/:id` gains with this spec. */
export interface SubscriptionContractFields {
  endNoticeDays?: number;
  lines?: SubscriptionLine[];
  coveredAccounts?: SubscriptionCoveredAccount[];
  ceiling?: SubscriptionCeiling | null;
}

/** `PUT /v4/subscriptions/:id` gains `endNoticeDays`. */
export interface SubscriptionContractUpdate {
  endNoticeDays?: number;
}

/** Today as YYYY-MM-DD in America/Montreal. */
export function montrealToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Montreal' }).format(new Date());
}

/** A line is active while it has no end date or ends in the future (D6). */
export function isLineActive(line: Pick<SubscriptionLine, 'endedAt'>, today = montrealToday()): boolean {
  return !line.endedAt || line.endedAt.slice(0, 10) > today;
}

/** True when the renewal must fall back to the invoice copy because an active line is unverified (D6). */
export function hasUnverifiedActiveLines(
  lines: Array<Pick<SubscriptionLine, 'endedAt' | 'verified'>>,
  today = montrealToday(),
): boolean {
  return lines.some((l) => isLineActive(l, today) && !l.verified);
}
